import { Fragment, ReactNode, createElement, useCallback, useMemo, useState } from 'react'

import { HTML_BLOCK_POLICY } from '../../consts/html-block-policy'
import {
  MarkdownElementNode,
  MarkdownRenderNode,
  parseMarkdownNodes,
} from '../../utils/markdown'
import CodeBlock from './code-block'
import HtmlBlockFrame from './html-block-frame'
import HtmlBlockPlaceholder from './html-block-placeholder'
import HtmlBlockPendingPlaceholder from './html-block-pending-placeholder'

type MarkdownRendererProps = {
  source: string
}

const VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'wbr'])

const ATTRIBUTE_NAMES: Record<string, string> = {
  class: 'className',
  colspan: 'colSpan',
  rowspan: 'rowSpan',
}

const toElementProps = (attrs: Record<string, string>, key: string): Record<string, unknown> => {
  const props: Record<string, unknown> = { key }

  Object.keys(attrs).forEach((name) => {
    props[ATTRIBUTE_NAMES[name] ?? name] = attrs[name]
  })

  return props
}

/**
 * Render the markdown token tree as React elements. HTML fences become live frames at
 * their real nesting depth; nothing is injected as an HTML string.
 */
const MarkdownRenderer = ({ source }: MarkdownRendererProps) => {
  const [promotedHtmlBlocks, setPromotedHtmlBlocks] = useState<Set<string>>(() => new Set())

  const nodes = useMemo(
    () => parseMarkdownNodes(source, { renderHtmlBlocks: true }),
    [source]
  )

  const htmlBlockOrdinals = useMemo(() => {
    const ordinals = new Map<string, number>()
    let ordinal = 0

    const visit = (node: MarkdownRenderNode): void => {
      if (node.kind === 'html-block' || node.kind === 'html-block-pending') {
        ordinals.set(node.key, ordinal)
        ordinal += 1
        return
      }

      if (node.kind === 'element') {
        node.children.forEach(visit)
      }
    }

    nodes.forEach(visit)
    return ordinals
  }, [nodes])

  const shouldRenderHtmlBlock = useCallback(
    (key: string): boolean => {
      const ordinal = htmlBlockOrdinals.get(key)
      return (
        ordinal !== undefined &&
        (ordinal < HTML_BLOCK_POLICY.limits.blocksPerMessage || promotedHtmlBlocks.has(key))
      )
    },
    [htmlBlockOrdinals, promotedHtmlBlocks]
  )

  const promoteHtmlBlock = useCallback((key: string): void => {
    setPromotedHtmlBlocks((current) => new Set(current).add(key))
  }, [])

  const renderNode = useCallback(
    (node: MarkdownRenderNode, key: string): ReactNode => {
      if (node.kind === 'text') {
        return node.value
      }

      if (node.kind === 'code') {
        return <CodeBlock key={node.key} code={node.code} language={node.language} />
      }

      if (node.kind === 'html-block-pending') {
        return <HtmlBlockPendingPlaceholder key={node.key} />
      }

      if (node.kind === 'html-block') {
        if (shouldRenderHtmlBlock(node.key)) {
          return <HtmlBlockFrame key={node.key} source={node.source} />
        }

        return (
          <HtmlBlockPlaceholder
            key={node.key}
            source={node.source}
            reason="limit"
            onRender={() => promoteHtmlBlock(node.key)}
          />
        )
      }

      const element = node as MarkdownElementNode
      const props = toElementProps(element.attrs, key)

      if (VOID_TAGS.has(element.tag) || element.children.length === 0) {
        return createElement(element.tag, props)
      }

      return createElement(
        element.tag,
        props,
        element.children.map((child, index) => renderNode(child, `${key}-${index}`))
      )
    },
    [promoteHtmlBlock, shouldRenderHtmlBlock]
  )

  return <Fragment>{nodes.map((node, index) => renderNode(node, `md-${index}`))}</Fragment>
}

export default MarkdownRenderer
